import { useEffect } from "react";

import { Button } from "../ui/button";
import { TOUR_STEPS, useTour } from "./tourContext";

// In-house product-tour overlay (L0-U10 session 2). Renders only while a tour
// is active. The active sidebar nav entry gets a highlight ring (driven by the
// shared tour context); this card explains the surface and steps through them.
// A light scrim sits behind the card with pointer-events disabled so the ringed
// nav item stays visible and the rest of the app is gently de-emphasised.
export function ProductTour() {
  const { isActive, step, totalSteps, nextStep, prevStep, finishTour, skipTour } =
    useTour();

  useEffect(() => {
    if (!isActive) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") skipTour();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isActive, skipTour]);

  if (!isActive) return null;

  const current = TOUR_STEPS[step];
  if (current === undefined) return null;

  const isFirst = step === 0;
  const isLast = step === totalSteps - 1;

  return (
    <div
      data-testid="product-tour"
      role="dialog"
      aria-modal="false"
      aria-labelledby="product-tour-title"
      className="fixed inset-0 z-50"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-black/30 pointer-events-none"
      />
      <div className="pointer-events-auto absolute left-1/2 top-24 w-[22rem] max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-card border border-outline-variant bg-surface-container p-5 shadow-lg">
        <p className="text-xs font-medium uppercase tracking-wide text-on-surface-muted">
          Step {step + 1} of {totalSteps}
        </p>
        <h2
          id="product-tour-title"
          className="mt-1 font-display text-lg font-medium tracking-tight text-on-surface"
        >
          {current.title}
        </h2>
        <p className="mt-2 text-sm text-on-surface-variant">{current.body}</p>
        <div className="mt-5 flex items-center justify-between gap-2">
          <Button type="button" variant="ghost" onClick={skipTour}>
            Skip tour
          </Button>
          <div className="flex items-center gap-2">
            {!isFirst && (
              <Button type="button" variant="outline" onClick={prevStep}>
                Back
              </Button>
            )}
            {isLast ? (
              <Button type="button" onClick={finishTour}>
                Done
              </Button>
            ) : (
              <Button type="button" onClick={nextStep}>
                Next
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
